import { Team, TeamWithMemberCount, CreateTeamRequest, UpdateTeamRequest } from '../types/teams';
import { api } from './api';

class TeamService {
  async getTeams(): Promise<Team[]> {
    // Remover prefixo /api já que a base URL já inclui
    const url = '/teams';
    
    try {
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('[teamService.getTeams] Erro na requisição:', error);
      console.error('[teamService.getTeams] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        headers: error.config?.headers
      });
      throw error;
    }
  }

  async getTeamsWithMemberCount(): Promise<TeamWithMemberCount[]> {
    const url = '/teams/with-member-count';
    
    try {
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('[teamService.getTeamsWithMemberCount] Erro na requisição:', error);
      console.error('[teamService.getTeamsWithMemberCount] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        headers: error.config?.headers
      });
      throw error;
    }
  }

  async getTeam(id: string): Promise<Team | undefined> {
    try {
      const response = await api.get(`/teams/${id}`);
      return response.data;
    } catch (error) {
      console.error('[teamService.getTeam] Erro ao buscar equipe:', error);
      return undefined;
    }
  }

  async createTeam(team: CreateTeamRequest): Promise<Team> {
    const response = await api.post('/teams', team);
    return response.data;
  }

  async updateTeam(id: string, team: UpdateTeamRequest): Promise<Team> {
    // Validação do formato do ID
    if (!id || typeof id !== 'string') {
      throw new Error('ID da equipe é obrigatório e deve ser uma string');
    }

    // Verificar se é um GUID válido (formato básico)
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidRegex.test(id)) {
      console.warn('[teamService.updateTeam] ID não está no formato GUID:', id);
    }

    const updateData = {
      id: id.toString(),
      name: team.name,
      description: team.description,
      isActive: team.isActive
    };
    
    
    try {
      const response = await api.put(`/teams/${id}`, updateData, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('[teamService.updateTeam] Erro na requisição:', error);
      console.error('[teamService.updateTeam] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      throw error;
    }
  }

  async deleteTeam(id: string): Promise<void> {
    await api.delete(`/teams/${id}`);
  }

  async addMember(teamId: string, userId: string, isBoss: boolean = false): Promise<Team> {
    const url = `/teams/${teamId}/members/${userId}?isBoss=${isBoss}`;
    
    try {
      const response = await api.post(url);
      return response.data;
    } catch (error: any) {
      console.error('[teamService.addMember] Erro na requisição:', error);
      console.error('[teamService.addMember] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async updateTeamMember(teamId: string, userId: string, isBoss: boolean): Promise<Team> {
    const url = `/teams/${teamId}/members/${userId}?isBoss=${isBoss}`;
    
    try {
      const response = await api.put(url);
      return response.data;
    } catch (error: any) {
      console.error('[teamService.updateTeamMember] Erro na requisição:', error);
      console.error('[teamService.updateTeamMember] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async removeMember(teamId: string, userId: string): Promise<Team> {
    const url = `/teams/${teamId}/members/${userId}`;
    
    try {
      const response = await api.delete(url);
      return response.data;
    } catch (error: any) {
      console.error('[teamService.removeMember] Erro na requisição:', error);
      console.error('[teamService.removeMember] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async updateTeamLeader(teamId: string, userId: string): Promise<Team> {
    const url = `/teams/${teamId}/leader`;
    
    try {
      const response = await api.put(url, { userId });
      return response.data;
    } catch (error: any) {
      console.error('[teamService.updateTeamLeader] Erro na requisição:', error);
      console.error('[teamService.updateTeamLeader] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }
}

export const teamService = new TeamService(); 